import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Check, ChevronsUpDown, Home, Loader2, Search } from 'lucide-react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../Common/dropdown-menu';
import organizationService from '../../services/organizationService';
import { useAuthStore } from '../../stores/authStore';
import { useSwitchOrganization } from '../../hooks/useSwitchOrganization';
import { cn } from '../../lib/utils';
import { TOPBAR_FIELD } from './topbarField';
import type { Organization } from '../../types';

interface OrganizationSwitcherProps {
  /** Name of the current organization, shown on the trigger. */
  orgName?: string;
  className?: string;
}

/** Super admin only (not while impersonating): moves the super admin into another organization. */
export const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ orgName, className }) => {
  const { t } = useTranslation();
  const currentOrgId = useAuthStore((s) => s.user?.organization_id ?? null);
  const { switchTo, switching } = useSwitchOrganization();
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [query, setQuery] = useState('');

  // Fetched on each opening so orgs created meanwhile show up.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadFailed(false);
    organizationService
      .getOrganizations({ includeInactive: true })
      .then((list) => {
        if (!cancelled) setOrgs(list);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (orgs ?? [])
      // A super admin never lives in a demo org (the backend refuses it too).
      .filter((o) => !o.is_demo && !o.is_demo_template)
      .filter((o) => !q || o.name.toLowerCase().includes(q))
      .sort((a, b) => {
        if (!!a.is_platform !== !!b.is_platform) return a.is_platform ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [orgs, query]);

  const handleSelect = async (org: Organization) => {
    if (org.id === currentOrgId) return;
    await switchTo(org.id);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(TOPBAR_FIELD, 'text-muted-foreground', className)}
          title={t('orgSwitcher.title')}
          disabled={switching}
        >
          {switching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
          ) : (
            <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
          )}
          <span className="text-xs truncate">{orgName || t('orgSwitcher.title')}</span>
          <ChevronsUpDown className="ml-auto h-3.5 w-3.5 flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('orgSwitcher.title')}
        </div>
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // Radix menus use typeahead on keystrokes; keep them for the search field.
            onKeyDown={(e) => e.stopPropagation()}
            placeholder={t('orgSwitcher.searchPlaceholder')}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-1">
          {orgs === null && !loadFailed && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {loadFailed && <div className="px-2 py-3 text-sm text-destructive">{t('orgSwitcher.error')}</div>}
          {orgs !== null &&
            visible.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onSelect={() => void handleSelect(org)}
                className={cn('gap-2 cursor-pointer', org.is_platform && 'font-medium')}
              >
                {org.is_platform ? (
                  <Home className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <Check className={cn('h-4 w-4 flex-shrink-0', org.id !== currentOrgId && 'invisible')} />
                )}
                <span className="truncate flex-1">
                  {org.is_platform ? t('orgSwitcher.backToPlatform', { name: org.name }) : org.name}
                </span>
                {!org.is_active && (
                  <span className="rounded-full border border-border px-1.5 text-[10px] text-muted-foreground">
                    {t('orgSwitcher.inactive')}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrganizationSwitcher;
